import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS } from "@/data/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));
  const variant = scrolled ? "light" : "dark";
  const linkColor = scrolled ? "text-navy/80" : "text-white/85";
  const linkHover = scrolled ? "hover:text-navy" : "hover:text-white";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass-light shadow-[0_1px_0_rgba(8,28,58,0.06),0_10px_30px_rgba(8,28,58,0.08)]" : "bg-transparent"
      }`}
    >
      <div className="ssa-container">
        <div className="flex items-center justify-between h-16 lg:h-[76px]">
          <Logo variant={variant} testId="site-navbar-logo" />

          <nav className="hidden lg:flex items-center gap-8" data-testid="site-navbar-primary-links">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to === "/" ? "home" : link.to.replace("/", "")}`}
                className={`relative text-sm font-medium transition-colors duration-200 ${
                  isActive(link.to)
                    ? scrolled
                      ? "text-royal"
                      : "text-white"
                    : `${linkColor} ${linkHover}`
                }`}
              >
                {link.label}
                {isActive(link.to) && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute left-0 -bottom-2 h-[2px] w-full"
                    style={{ background: "var(--ssa-turquoise)" }}
                  />
                )}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center">
            <Link
              to="/contact"
              data-testid="site-navbar-primary-cta"
              className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-sm font-medium transition-colors duration-200 ${
                scrolled
                  ? "bg-navy text-white hover:bg-[#0B254B]"
                  : "border border-white/40 text-white hover:bg-white/12"
              }`}
            >
              Contact <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="site-navbar-mobile-menu-button"
            aria-label="Open menu"
            className={`lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl transition-colors ${
              scrolled ? "text-navy hover:bg-black/5" : "text-white hover:bg-white/10"
            }`}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              className="fixed top-0 right-0 z-50 h-full w-[82%] max-w-sm navy-gradient grain overflow-hidden lg:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid="mobile-menu"
            >
              <div className="relative flex items-center justify-between px-6 h-16 border-b border-white/10">
                <Logo variant="dark" />
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="inline-flex items-center justify-center h-10 w-10 rounded-xl text-white hover:bg-white/10"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <nav className="relative px-6 py-8 flex flex-col gap-1">
                {NAV_LINKS.map((link, i) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.05 }}
                  >
                    <Link
                      to={link.to}
                      onClick={() => setOpen(false)}
                      className={`flex items-center justify-between py-3 text-lg font-display border-b border-white/8 ${
                        isActive(link.to) ? "text-turquoise" : "text-white/90"
                      }`}
                    >
                      {link.label}
                      <ArrowUpRight className="h-5 w-5 opacity-50" />
                    </Link>
                  </motion.div>
                ))}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};
